import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

const TaskDetailsModal = ({ isOpen, onClose, task, onStatusUpdate, onAddComment }) => {
  const [comment, setComment] = useState('');
  const [isAddingComment, setIsAddingComment] = useState(false);

  const statusOptions = [
    { value: 'pending', label: 'Pending' },
    { value: 'in progress', label: 'In Progress' },
    { value: 'completed', label: 'Completed' },
    { value: 'on hold', label: 'On Hold' }
  ];

  const getPriorityColor = (priority) => {
    switch (priority?.toLowerCase()) {
      case 'high':
        return 'text-error bg-error/10 border-error/20';
      case 'medium':
        return 'text-warning bg-warning/10 border-warning/20';
      case 'low':
        return 'text-success bg-success/10 border-success/20';
      default:
        return 'text-muted-foreground bg-muted border-border';
    }
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'completed':
        return 'text-success bg-success/10 border-success/20';
      case 'in progress':
        return 'text-primary bg-primary/10 border-primary/20';
      case 'pending':
        return 'text-warning bg-warning/10 border-warning/20';
      case 'on hold':
        return 'text-muted-foreground bg-muted border-border';
      default:
        return 'text-muted-foreground bg-muted border-border';
    }
  };

  const handleStatusChange = (newStatus) => {
    onStatusUpdate(task?.id, newStatus);
  };

  const handleAddComment = () => {
    if (comment?.trim()) {
      onAddComment(task?.id, comment?.trim());
      setComment('');
      setIsAddingComment(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString)?.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (!isOpen || !task) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-400 flex items-center justify-center p-4">
      <div className="bg-card border border-border rounded-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto card-shadow">
        <div className="flex items-center justify-between p-6 border-b border-border">
          <h2 className="text-xl font-semibold text-foreground">Task Details</h2>
          <Button
            variant="ghost"
            size="sm"
            iconName="X"
            onClick={onClose}
          />
        </div>

        <div className="p-6 space-y-6">
          {/* Task Header */}
          <div>
            <h3 className="text-lg font-semibold text-foreground mb-2">{task?.title}</h3>
            <p className="text-muted-foreground mb-4">{task?.description}</p>
            
            <div className="flex items-center space-x-4">
              <span className={`px-3 py-1 rounded-full text-sm font-medium border ${getPriorityColor(task?.priority)}`}>
                {task?.priority} Priority
              </span>
              <span className={`px-3 py-1 rounded-full text-sm font-medium border ${getStatusColor(task?.status)}`}>
                {task?.status}
              </span>
            </div>
          </div>

          {/* Task Information */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium text-foreground">Project</label>
                <p className="text-sm text-muted-foreground mt-1">{task?.project}</p>
              </div>
              
              <div>
                <label className="text-sm font-medium text-foreground">Assigned By</label>
                <p className="text-sm text-muted-foreground mt-1">{task?.assignedBy}</p>
              </div>
              
              <div>
                <label className="text-sm font-medium text-foreground">Created Date</label>
                <p className="text-sm text-muted-foreground mt-1">{formatDate(task?.createdDate)}</p>
              </div>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium text-foreground">Due Date</label>
                <p className="text-sm text-muted-foreground mt-1">{formatDate(task?.dueDate)}</p>
              </div>
              
              <div>
                <label className="text-sm font-medium text-foreground">Estimated Hours</label>
                <p className="text-sm text-muted-foreground mt-1">{task?.estimatedHours} hours</p>
              </div>
              
              {task?.timeLogged && (
                <div>
                  <label className="text-sm font-medium text-foreground">Time Logged</label>
                  <p className="text-sm text-muted-foreground mt-1">{task?.timeLogged}</p>
                </div>
              )}
            </div>
          </div>

          {/* Status Update */}
          <div>
            <label className="text-sm font-medium text-foreground mb-2 block">Update Status</label>
            <Select
              options={statusOptions}
              value={task?.status?.toLowerCase()}
              onChange={handleStatusChange}
              className="max-w-xs"
            />
          </div>

          {/* Attachments */}
          {task?.attachments && task?.attachments?.length > 0 && (
            <div>
              <label className="text-sm font-medium text-foreground mb-2 block">Attachments</label>
              <div className="space-y-2">
                {task?.attachments?.map((attachment, index) => (
                  <div key={index} className="flex items-center space-x-2 p-2 bg-muted/50 rounded">
                    <Icon name="Paperclip" size={16} className="text-muted-foreground" />
                    <span className="text-sm text-foreground">{attachment?.name}</span>
                    <Button variant="ghost" size="xs" iconName="Download">
                      Download
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Comments Section */}
          <div>
            <div className="flex items-center justify-between mb-4">
              <label className="text-sm font-medium text-foreground">Comments</label>
              <Button
                variant="outline"
                size="sm"
                iconName="Plus"
                onClick={() => setIsAddingComment(true)}
              >
                Add Comment
              </Button>
            </div>

            {isAddingComment && (
              <div className="mb-4 p-4 bg-muted/50 rounded-lg">
                <Input
                  type="text"
                  placeholder="Add your comment..."
                  value={comment}
                  onChange={(e) => setComment(e?.target?.value)}
                  className="mb-3"
                />
                <div className="flex items-center space-x-2">
                  <Button
                    variant="default"
                    size="sm"
                    onClick={handleAddComment}
                  >
                    Add Comment
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setIsAddingComment(false);
                      setComment('');
                    }}
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            )}

            <div className="space-y-3 max-h-40 overflow-y-auto">
              {task?.comments && task?.comments?.length > 0 ? (
                task?.comments?.map((comment, index) => (
                  <div key={index} className="p-3 bg-muted/30 rounded">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm font-medium text-foreground">{comment?.author}</span>
                      <span className="text-xs text-muted-foreground">{formatDate(comment?.timestamp)}</span>
                    </div>
                    <p className="text-sm text-muted-foreground">{comment?.text}</p>
                  </div>
                ))
              ) : (
                <p className="text-sm text-muted-foreground text-center py-4">No comments yet</p>
              )}
            </div>
          </div>
        </div>

        <div className="flex items-center justify-end space-x-3 p-6 border-t border-border">
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
          {task?.status !== 'Completed' && (
            <Button
              variant="default"
              iconName="CheckCircle"
              onClick={() => handleStatusChange('completed')}
            >
              Mark Complete
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default TaskDetailsModal;